import _ from "lodash";
import { QMS } from "../_constants/actionConstants";
import { getReactFlowEdges, getReactFlowNodes } from "./helpers/qmsWorkflowHelpers";

const qmsInitialState = {
  openIssueLists: {}, // Normalized by tabId
  openIssues: {}, // Normalized by issueId
  openIssueStages: {}, // Normalized by issueId. For each issueId, only one issueStage is stored.
  qmsUsersForIssue: {}, // Normalized by issueId
  divisions: [],
  cmtCategories: {}, // Normalized by divisionId
  cmtStages: {}, // Normalized by divisionId
  openWorkflowLists: {}, // Normalized by tabId
  openWorkflows: {}, // Normalized by workflowId
  qmsFrameworkChanges: {}, // Normalized by divisionId
  qmsDocumentTypes: {}, // Normalized by divisionId
  qmsDocuments: {}, // Normalized by divisionId
  qmsTeams: {}, // Normalized by divisionId. Lists QmsTeamListDtos by division.
  openQmsTeams: {}, // Normalized by qmsTeamId
  qmsTeamMembers: {}, // Normalized by qmsTeamId
  qmsTeamMemberRoles: [],
  qmsRolePermissions: {}, // Role permissions for the current user
  userRoles: null,
  userFunctionalRoles: [],
  relationshipTypes: [], // For workflow
  permissionScopes: {}, // Normalized by name
  actions: {}, // Normalized by name
  authorizeLevel: null, // function
  qmsUser: null,
  miscSessionData: {},
  fetching: false,
  adding: false,
};

export const reducer = (state = qmsInitialState, action) => {
  switch (action.type) {
    case QMS.PERMISSIONS.FETCH_FOR_USER:
    case QMS.PERMISSIONS.FETCH_PERMISSION_SCOPES_ACTIONS:
    case QMS.ISSUE.FETCH:
    case QMS.ISSUE.FETCH_ALL:
    case QMS.ISSUE_STAGE.FETCH:
    case QMS.ISSUE_STAGE.ADD:
    case QMS.ISSUE_STAGE.ADD_POST:
    case QMS.DIVISIONS.ADD:
    case QMS.DIVISIONS.FETCH_ALL:
    case QMS.DIVISIONS.REMOVE:
    case QMS.DIVISIONS.UPDATE:
    case QMS.CMT_CATEGORY.ADD:
    case QMS.CMT_CATEGORY.FETCH:
    case QMS.CMT_CATEGORY.FETCH_ALL:
    case QMS.CMT_CATEGORY.REMOVE:
    case QMS.CMT_CATEGORY.UPDATE:
    case QMS.CMT_STAGE.ADD:
    case QMS.CMT_STAGE.FETCH:
    case QMS.CMT_STAGE.FETCH_ALL:
    case QMS.CMT_STAGE.REMOVE:
    case QMS.CMT_STAGE.UPDATE:
    case QMS.WORKFLOW.ADD:
    case QMS.WORKFLOW.FETCH:
    case QMS.WORKFLOW.FETCH_ALL:
    case QMS.WORKFLOW.REMOVE:
    case QMS.WORKFLOW.UPDATE:
    case QMS.WORKFLOW.FETCH_RELTYPES:
    case QMS.DOCUMENTS.ADD:
    case QMS.DOCUMENTS.FETCH:
    case QMS.DOCUMENTS.FETCH_ALL:
    case QMS.DOCUMENTS.REMOVE:
    case QMS.DOCUMENTS.UPDATE:
    case QMS.DOCUMENTTYPES.ADD:
    case QMS.DOCUMENTTYPES.FETCH_ALL:
    case QMS.DOCUMENTTYPES.REMOVE:
    case QMS.DOCUMENTTYPES.UPDATE:
    case QMS.TEAM.ADD:
    case QMS.TEAM.FETCH:
    case QMS.TEAM.FETCH_ALL:
    case QMS.TEAM.REMOVE:
    case QMS.TEAM.UPDATE:
    case QMS.TEAM_MEMBER_ROLE.ADD:
    case QMS.TEAM_MEMBER_ROLE.FETCH:
    case QMS.TEAM_MEMBER_ROLE.FETCH_ALL:
    case QMS.TEAM_MEMBER_ROLE.REMOVE:
    case QMS.TEAM_MEMBER_ROLE.UPDATE:
    case QMS.USER.ADD:
    case QMS.USER.FETCH:
    case QMS.USER.FETCH_ALL:
    case QMS.USER.FETCH_ALL_FOR_ISSUE:
    case QMS.USER.REMOVE:
    case QMS.USER.UPDATE:
      return { ...state, fetching: true };

    case QMS.ISSUE.ADD:
      return { ...state, adding: true };

    case QMS.ISSUE.FETCH_FULFILLED: {
      const { issueId, issue } = action;
      return {
        ...state,
        openIssues:
          issueId in state.openIssues
            ? {
                ...state.openIssues,
                [issueId]: { ...state.openIssues[issueId], ...issue },
              }
            : { ...state.openIssues, [issueId]: issue },
        fetching: false,
      };
    }

    case QMS.ISSUE.UPDATE_FULFILLED: {
      const { issueId, updatedIssue } = action;
      if (!issueId || !(issueId in state.openIssues)) return;
      return {
        ...state,
        openIssues: { ...state.openIssues, [issueId]: updatedIssue },
        fetching: false,
      };
    }

    case QMS.ISSUE.UPDATE_CLIENT: {
      const { issueId, fieldsToUpdate } = action;
      if (!issueId || !(issueId in state.openIssues)) return;

      return {
        ...state,
        openIssues: {
          ...state.openIssues,
          [issueId]: { ...state.openIssues[issueId], ...fieldsToUpdate },
        },
      };
    }

    case QMS.ISSUE.ACTION_ITEMS.UPDATE_CLIENT: {
      const { issueId, issueActionItemId, fieldsToUpdate } = action;
      if (!issueId || !(issueId in state.openIssues) || !issueActionItemId) return;

      const actionItems = [...(state.openIssues[issueId].actionItems || [])];
      const actionItemToUpdate = actionItems.find(
        (ai) => ai.issueActionItemId === issueActionItemId
      );
      if (!actionItemToUpdate) return;
      for (const fieldToUpdate in fieldsToUpdate) {
        if (fieldToUpdate in actionItemToUpdate)
          actionItemToUpdate[fieldToUpdate] = fieldsToUpdate[fieldToUpdate];
      }

      return {
        ...state,
        openIssues: {
          ...state.openIssues,
          [issueId]: { ...state.openIssues[issueId], actionItems },
        },
      };
    }

    case QMS.ISSUE_STAGE.ADD_FULFILLED:
    case QMS.ISSUE_STAGE.FETCH_FULFILLED:
    case QMS.ISSUE_STAGE.ADD_POST_FULFILLED:
    case QMS.ISSUE_STAGE.ADD_POST_UPVOTE_FULFILLED:
    case QMS.ISSUE_STAGE.ADD_POST_DOWNVOTE_FULFILLED:
    case QMS.ISSUE_STAGE.UPDATE_FULFILLED: {
      const { issueId, issueStage } = action;
      return {
        ...state,
        openIssueStages: {
          ...state.openIssueStages,
          [issueId]: issueStage,
        },
        fetching: false,
      };
    }

    case QMS.ISSUE_STAGE.CLEAR:
    case QMS.ISSUE_STAGE.REMOVE_FULFILLED: {
      const { issueId } = action;
      if (!issueId || !(issueId in state.openIssueStages)) return { ...state };

      const _openIssueStages = _.cloneDeep(state.openIssueStages);
      delete _openIssueStages[issueId];

      return {
        ...state,
        openIssueStages: _openIssueStages,
        fetching: false,
      };
    }

    case QMS.ISSUE_STAGE.UPDATE_CLIENT: {
      const { issueId, fieldsToUpdate } = action;
      if (!issueId || !fieldsToUpdate || !(issueId in state.openIssueStages))
        return { ...state };
      const _issueStageToUpdate = _.cloneDeep(state.openIssueStages[issueId]);
      for (const field in fieldsToUpdate) {
        _issueStageToUpdate[field] = fieldsToUpdate[field];
      }
      return {
        ...state,
        openIssueStages: { ...state.openIssueStages, [issueId]: _issueStageToUpdate },
      };
    }

    case QMS.ISSUE.CLOSE_FULFILLED: {
      const { issueId, issue } = action;
      return {
        ...state,
        openIssues: { ...state.openIssues, [issueId]: issue },
        fetching: false,
      };
    }

    case QMS.ISSUE.FETCH_ALL_FULFILLED: {
      const { tabId, issueList } = action;
      return {
        ...state,
        openIssueLists: {
          ...state.openIssueLists,
          [tabId]: {
            issueList,
            childIssueLists: {
              ...(tabId in state.openIssueLists &&
              "childIssueLists" in state.openIssueLists[tabId]
                ? state.openIssueLists[tabId].childIssueLists
                : {}),
            },
          },
        },
        fetching: false,
      };
    }

    case QMS.ISSUE.FETCH_CHILDREN_FULFILLED: {
      const { tabId, childIssueList, issueId } = action;
      if (!tabId || !childIssueList || !issueId) return { ...state };

      return {
        ...state,
        openIssueLists: {
          ...state.openIssueLists,
          [tabId]: {
            issueList: [
              ...(tabId in state.openIssueLists &&
              "issueList" in state.openIssueLists[tabId]
                ? state.openIssueLists[tabId].issueList || []
                : []),
            ],
            childIssueLists: {
              ...(tabId in state.openIssueLists &&
              "childIssueLists" in state.openIssueLists[tabId]
                ? state.openIssueLists[tabId].childIssueLists
                : {}),
              [issueId]: childIssueList,
            },
          },
        },
        fetching: false,
      };
    }

    case QMS.DIVISIONS.FETCH_ALL_FULFILLED: {
      const { divisions } = action;
      return { ...state, divisions, fetching: false };
    }

    case QMS.DOCUMENTS.FETCH_ALL_FULFILLED:
    case QMS.DOCUMENTS.ADD_FULFILLED:
    case QMS.DOCUMENTS.UPDATE_FULFILLED:
    case QMS.DOCUMENTS.REMOVE_FULFILLED: {
      const { divisionId, qmsDocuments } = action;
      if (!divisionId || !qmsDocuments) return { ...state };
      return {
        ...state,
        qmsDocuments: { ...state.qmsDocuments, [divisionId]: qmsDocuments },
        fetching: false,
      };
    }

    case QMS.DOCUMENTTYPES.FETCH_ALL_FULFILLED:
    case QMS.DOCUMENTTYPES.ADD_FULFILLED:
    case QMS.DOCUMENTTYPES.UPDATE_FULFILLED:
    case QMS.DOCUMENTTYPES.REMOVE_FULFILLED: {
      const { divisionId, qmsDocumentTypes } = action;
      if (!divisionId || !qmsDocumentTypes) return { ...state };
      return {
        ...state,
        qmsDocumentTypes: { ...state.qmsDocumentTypes, [divisionId]: qmsDocumentTypes },
        fetching: false,
      };
    }

    case QMS.CMT_CATEGORY.FETCH_ALL_FULFILLED:
    case QMS.CMT_CATEGORY.ADD_FULFILLED:
    case QMS.CMT_CATEGORY.REMOVE_FULFILLED: {
      const { divisionId, cmtCategories } = action;
      return {
        ...state,
        cmtCategories: { ...state.cmtCategories, [divisionId]: cmtCategories },
        fetching: false,
      };
    }

    case QMS.CMT_STAGE.FETCH_ALL_FULFILLED:
    case QMS.CMT_STAGE.ADD_FULFILLED:
    case QMS.CMT_STAGE.UPDATE_FULFILLED:
    case QMS.CMT_STAGE.REMOVE_FULFILLED: {
      const { divisionId, cmtStages } = action;
      if (!divisionId || !cmtStages) return { ...state, fetching: false };
      return {
        ...state,
        cmtStages: { ...state.cmtStages, [divisionId]: cmtStages },
        fetching: false,
      };
    }

    case QMS.WORKFLOW.FETCH_FULFILLED: {
      const { workflowId, workflow } = action;
      if (!workflowId || !workflow) return { ...state, fetching: false };

      const reactFlowNodes = getReactFlowNodes(workflow.nodes || []);
      const reactFlowEdges = getReactFlowEdges(workflow.edges || []);

      return {
        ...state,
        openWorkflows: {
          ...state.openWorkflows,
          [workflowId]: { ...workflow, nodes: reactFlowNodes, edges: reactFlowEdges },
        },
        fetching: false,
      };
    }

    case QMS.WORKFLOW.FETCH_ALL_FULFILLED:
    case QMS.WORKFLOW.UPDATE_FULFILLED:
    case QMS.WORKFLOW.REMOVE_FULFILLED: {
      const { tabId, workflowList } = action;
      if (!tabId || !workflowList) return { ...state, fetching: false };

      return {
        ...state,
        openWorkflowLists: {
          ...state.openWorkflowLists,
          [tabId]: workflowList,
        },
        fetching: false,
      };
    }

    case QMS.WORKFLOW.FETCH_RELTYPES_FULFILLED: {
      const { relationshipTypes } = action;
      if (!relationshipTypes) return { ...state, fetching: false };

      return {
        ...state,
        relationshipTypes,
        fetching: false,
      };
    }

    case QMS.WORKFLOW.EDIT: {
      const { workflowId, fieldsToEdit } = action;
      if (!workflowId || !fieldsToEdit || !(workflowId in state.openWorkflows))
        return { ...state };
      const _workflow = _.cloneDeep(state.openWorkflows[workflowId]);
      for (const field in fieldsToEdit) {
        _workflow[field] = fieldsToEdit[field];
      }

      return {
        ...state,
        openWorkflows: {
          ...state.openWorkflows,
          [workflowId]: _workflow,
        },
      };
    }

    case QMS.WORKFLOW.CLEAR: {
      const { workflowId } = action;
      const _openWorkflows = _.cloneDeep(state.openWorkflows);
      if (!(workflowId in _openWorkflows)) return { ...state };
      delete _openWorkflows[workflowId];
      return {
        ...state,
        openWorkflows: _openWorkflows,
      };
    }

    case QMS.CMT_QMSFC.ADD_FULFILLED:
    case QMS.CMT_QMSFC.REMOVE_FULFILLED:
    case QMS.CMT_QMSFC.UPDATE_FULFILLED:
    case QMS.CMT_QMSFC.FETCH_ALL_FULFILLED: {
      const { qmsFrameworkChanges, divisionId } = action;
      return {
        ...state,
        qmsFrameworkChanges: {
          ...state.qmsFrameworkChanges,
          [divisionId]: qmsFrameworkChanges,
        },
        fetching: false,
      };
    }

    case QMS.TEAM.FETCH_FULFILLED: {
      const { qmsTeam } = action;
      if (!qmsTeam) return { ...state };
      return {
        ...state,
        openQmsTeams: {
          ...state.openQmsTeams,
          [qmsTeam.qmsTeamId]: qmsTeam,
        },
        fetching: false,
      };
    }

    case QMS.TEAM.FETCH_ALL_FULFILLED:
    case QMS.TEAM.UPDATE_FULFILLED:
    case QMS.TEAM.REMOVE_FULFILLED: {
      const { divisionId, qmsTeams } = action;
      return {
        ...state,
        qmsTeams: { ...state.qmsTeams, [divisionId]: qmsTeams },
        fetching: false,
      };
    }

    case QMS.TEAM.FETCH_MEMBERS_FULFILLED: {
      const { qmsTeamId, members } = action;
      return {
        ...state,
        qmsTeamMembers: { ...state.qmsTeamMembers, [qmsTeamId]: members },
        fetching: false,
      };
    }

    case QMS.TEAM_MEMBER_ROLE.FETCH_ALL_FULFILLED: {
      const { qmsTeamMemberRoles } = action;
      return { ...state, qmsTeamMemberRoles, fetching: false };
    }

    case QMS.USER.FETCH_FULFILLED: {
      const { qmsUser } = action;
      return { ...state, qmsUser, fetching: false };
    }

    case QMS.USER.CLEAR:
      return { ...state, qmsUser: null };

    case QMS.USER.FETCH_ALL_FOR_ISSUE_FULFILLED: {
      const { issueId, qmsUsers } = action;
      return {
        ...state,
        qmsUsersForIssue: { ...state.qmsUsersForIssue, [issueId]: qmsUsers },
        fetching: false,
      };
    }

    case QMS.USER.FETCH_ROLES_FULFILLED: {
      const { userRoles } = action;
      const _userRoles = {};
      (userRoles || []).forEach((ur) => (_userRoles[ur.value] = ur));
      return { ...state, userRoles: _userRoles, fetching: false };
    }

    case QMS.PERMISSIONS.FETCH_PERMISSION_SCOPES_ACTIONS_FULFILLED: {
      const { permissionScopes, actions } = action;
      const _permissionScopes = {};
      (permissionScopes || []).forEach((ps) => (_permissionScopes[ps.name] = ps));
      const _actions = {};
      (actions || []).forEach((a) => (_actions[a.name] = a));
      return {
        ...state,
        permissionScopes: _permissionScopes,
        actions: _actions,
        fetching: false,
      };
    }

    case QMS.USER.FETCH_FUNCTIONAL_ROLES_FULFILLED: {
      const { userFunctionalRoles } = action;
      return { ...state, userFunctionalRoles, fetching: false };
    }

    case QMS.USER.UPDATE_CLIENT: {
      const { qmsUser } = action;
      return { ...state, qmsUser };
    }

    case QMS.PERMISSIONS.FETCH_FOR_USER_FULFILLED: {
      const { qmsRolePermissions } = action;
      return { ...state, qmsRolePermissions, fetching: false };
    }

    case QMS.ISSUE.CLEAR_LIST: {
      const { tabId } = action;
      if (!(tabId in state.openIssueLists)) {
        return { ...state };
      }

      const _openIssueLists = _.cloneDeep(state.openIssueLists);
      delete _openIssueLists[tabId];
      return { ...state, openIssueLists: _openIssueLists };
    }

    case QMS.ISSUE.ADD_FULFILLED:
      return { ...state, adding: false };

    case QMS.ISSUE.ADD_CHILD_REJECTED:
    case QMS.ISSUE.ADD_CHILD_FULFILLED:
    case QMS.ISSUE.ADD_CHILD_REJECTED:
    case QMS.TEAM.FETCH_REJECTED:
      return { ...state, fetching: false };

    default:
      return state;
  }
};
